/**
 * D-2 — Sam proactif (front-only).
 *
 * Drives 2 ambient relances when the student lands on a Sam-wrapped
 * activity and stays silent. The hard cap of 2 auto bubbles per
 * activity session comes straight from SAM_PROACTIVE_KEY (teaser_t5 +
 * idle_t3min) — once both keys are acked, nothing else fires.
 *
 * Gating (`enabled`) is owned by the parent so the hook stays dumb. The
 * parent passes `false` once the copy is submitted: the end of the
 * activity (wait, challenge, correction) is not a conversation.
 *
 * Passive ack: as soon as the student opens the sheet OR sends any
 * message, both keys are persisted as acked so re-mounts (back to
 * dashboard then re-tap the activity) don't re-trigger the teasers
 * for the rest of the session.
 *
 * Déplacé tel quel de `student/sam-sessions/hooks/` : mêmes horloges ; seuls les chemins et des types
 * changent.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { SAM_BUBBLE_ORIGIN, SAM_PROACTIVE_KEY } from "../../../data/constants/samConstants"
import { ackSamProactiveBubble, injectSamBubble } from "../../../data/redux/actions/data"

const TEASER_DELAY_MS = 5 * 1000
const IDLE_DELAY_MS = 3 * 60 * 1000

const TEASER_CONTENT = "Coucou, je suis Sam. Besoin d'un coup de main pour démarrer ?"
const IDLE_CONTENT = "Tu avances bien ? Je suis là si tu veux qu'on regarde un point ensemble."

const EMPTY_ACKS: Record<string, boolean> = {}

type ProactivityRoot = {
    data?: {
        sam?: {
            proactiveAcksByActivityId?: Record<string, Record<string, boolean>>
            conversationByActivityId?: Record<string, { messages?: { role?: string }[] }>
        }
    }
}

export function useSamProactivity({
    activityId,
    enabled,
    sheetOpen,
}: {
    activityId: number | null
    enabled: boolean
    sheetOpen: boolean
}) {
    const dispatch = useDispatch()

    const acks = useSelector(
        (state: ProactivityRoot) =>
            (activityId != null ? state.data?.sam?.proactiveAcksByActivityId?.[activityId] : undefined) || EMPTY_ACKS
    )

    const studentMessageCount = useSelector((state: ProactivityRoot) => {
        const msgs =
            (activityId != null ? state.data?.sam?.conversationByActivityId?.[activityId]?.messages : undefined) || []
        return msgs.filter((m) => m.role === "student").length
    })

    // Passive ack: any explicit interaction with Sam consumes both
    // teasers for the rest of the session. Idempotent — guarded by the
    // !acks[key] checks before dispatch.
    useEffect(() => {
        if (!activityId) return
        if (!sheetOpen && studentMessageCount === 0) return
        if (!acks[SAM_PROACTIVE_KEY.teaserT5]) {
            dispatch(ackSamProactiveBubble(activityId, SAM_PROACTIVE_KEY.teaserT5))
        }
        if (!acks[SAM_PROACTIVE_KEY.idleT3min]) {
            dispatch(ackSamProactiveBubble(activityId, SAM_PROACTIVE_KEY.idleT3min))
        }
    }, [activityId, sheetOpen, studentMessageCount, acks, dispatch])

    // Arm timers. Each timer self-acks on fire so the effect re-runs
    // and we never double-arm. Cleanup clears pending timers on unmount
    // or dependency change (e.g. enabled flipping false when D-1 kicks
    // in).
    useEffect(() => {
        if (!activityId || !enabled) return

        const fireBubble = (key: string, content: string) => {
            if (acks[key]) return
            dispatch(injectSamBubble(activityId, content, "encouraging", SAM_BUBBLE_ORIGIN.proactive))
            dispatch(ackSamProactiveBubble(activityId, key))
            amplitude.track("sam.proactive.bubble_injected", {
                activity_id: activityId,
                key,
            })
        }

        let teaserTimer: ReturnType<typeof setTimeout> | null = null
        let idleTimer: ReturnType<typeof setTimeout> | null = null

        if (!acks[SAM_PROACTIVE_KEY.teaserT5]) {
            teaserTimer = setTimeout(() => {
                fireBubble(SAM_PROACTIVE_KEY.teaserT5, TEASER_CONTENT)
            }, TEASER_DELAY_MS)
        }
        if (!acks[SAM_PROACTIVE_KEY.idleT3min]) {
            idleTimer = setTimeout(() => {
                fireBubble(SAM_PROACTIVE_KEY.idleT3min, IDLE_CONTENT)
            }, IDLE_DELAY_MS)
        }

        return () => {
            if (teaserTimer) clearTimeout(teaserTimer)
            if (idleTimer) clearTimeout(idleTimer)
        }
    }, [activityId, enabled, acks, dispatch])
}
